import React, { createContext, useCallback, useContext, useState } from "react";
import { Mail, KeyRound, ArrowRight, Loader2, ScanFace } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Field, Input } from "../ui/Primitives";
import { useI18n } from "../../i18n";
import { demoStart, demoVerify, demoResend } from "../../lib/demoApi";

const DemoCtx = createContext(null);

export function useDemo() {
  const ctx = useContext(DemoCtx);
  if (!ctx) throw new Error("useDemo must be used within DemoProvider");
  return ctx;
}

export function DemoProvider({ children }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("form"); // form | token | starting
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [consent, setConsent] = useState(false);
  const [demoId, setDemoId] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = useCallback(() => {
    setStep("form");
    setName("");
    setEmail("");
    setWhatsapp("");
    setConsent(false);
    setDemoId("");
    setToken("");
    setBusy(false);
    setError("");
  }, []);

  const openDemo = useCallback(() => {
    reset();
    setOpen(true);
  }, [reset]);

  const close = useCallback(() => {
    if (step === "starting") return; // don't interrupt the redirect
    setOpen(false);
  }, [step]);

  async function submitForm(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await demoStart({ name, email, language: lang, whatsapp });
      setDemoId(res.demo_id);
      setStep("token");
    } catch (err) {
      setError(t("demo.err.start"));
    } finally {
      setBusy(false);
    }
  }

  async function submitToken(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await demoVerify({ demoId, token: token.trim() });
      if (!res.verify_url) throw new Error("no verify_url");
      setStep("starting");
      window.location.href = res.verify_url;
    } catch (err) {
      setError(err.message || t("demo.err.token"));
      setBusy(false);
    }
  }

  async function resend() {
    setError("");
    try {
      await demoResend({ demoId });
    } catch {
      setError(t("demo.err.generic"));
    }
  }

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  // Optional field: empty is fine, but a typed number must look like a phone.
  const whatsappDigits = whatsapp.replace(/\D/g, "");
  const whatsappValid = !whatsappDigits || (whatsappDigits.length >= 10 && whatsappDigits.length <= 15);

  return (
    <DemoCtx.Provider value={{ openDemo }}>
      {children}
      <Modal
        open={open}
        onClose={close}
        title={step === "token" ? t("demo.token.title") : t("demo.modal.title")}
        description={step === "token" ? undefined : t("demo.modal.subtitle")}
      >
        {step === "form" && (
          <form onSubmit={submitForm} className="w-full space-y-4">
            <Field label={t("demo.form.name")}>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("demo.form.namePh")}
                autoFocus
                required
              />
            </Field>
            <Field label={t("demo.form.email")}>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("demo.form.emailPh")}
                required
              />
            </Field>
            <Field label={t("demo.form.whatsapp")} hint={t("demo.form.whatsappHint")}>
              <Input
                type="tel"
                inputMode="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder={t("demo.form.whatsappPh")}
                autoComplete="tel"
              />
            </Field>
            <label className="flex items-start gap-2.5 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
              />
              <span>{t("demo.form.consent")}</span>
            </label>
            {error && <p className="text-sm font-medium text-destructive">{error}</p>}
            <Button type="submit" className="w-full" loading={busy} disabled={!name || !emailValid || !whatsappValid || !consent}>
              <Mail className="h-4 w-4" /> {t("demo.form.submit")}
            </Button>
            <p className="rounded-lg bg-secondary/60 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
              {t("demo.note")}
            </p>
          </form>
        )}

        {step === "token" && (
          <form onSubmit={submitToken} className="w-full space-y-4">
            <p className="text-sm text-muted-foreground">
              {whatsappDigits
                ? t("demo.token.sentToBoth", null, { email, phone: whatsapp.trim() })
                : t("demo.token.sentTo", null, { email })}
            </p>
            <Field label={t("demo.token.label")}>
              <Input
                value={token}
                onChange={(e) => setToken(e.target.value.toUpperCase())}
                placeholder="A1B2C3"
                maxLength={6}
                autoFocus
                className="text-center text-lg font-mono tracking-[0.5em]"
              />
            </Field>
            {error && <p className="text-sm font-medium text-destructive">{error}</p>}
            <Button type="submit" className="w-full" loading={busy} disabled={token.trim().length !== 6}>
              <KeyRound className="h-4 w-4" /> {t("demo.token.submit")} <ArrowRight className="h-4 w-4" />
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setStep("form"); setError(""); }} className="text-muted-foreground hover:text-foreground">
                {t("demo.token.back")}
              </button>
              <button type="button" onClick={resend} className="font-semibold text-brand hover:underline">
                {t("demo.token.resend")}
              </button>
            </div>
          </form>
        )}

        {step === "starting" && (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/12 text-brand">
              <ScanFace className="h-8 w-8" />
              <Loader2 className="absolute h-16 w-16 animate-spin text-brand/40" />
            </span>
            <p className="text-sm text-muted-foreground">{t("demo.starting")}</p>
          </div>
        )}
      </Modal>
    </DemoCtx.Provider>
  );
}
